import { getDistance as haversineDist } from 'ol/sphere';
import { VoyageRoutePointFeature, VoyageRouteLineFeature, VoyageRoute } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { ImportResult } from './types/converters.types';

const NMToMeters = 1852; // Nautical miles -> meters
const KtsToMps = 1852/(60*60);

export class RTEConverter {
    static check(str: string) {
        try {
            const domParser = new DOMParser();
            var doc = domParser.parseFromString(str, "text/xml");

            const root = doc.firstElementChild;

            const pp = root!.querySelector('passagePlan');

            if (root && root.nodeName === 'route' && pp) {
                return doc;
            }
        } catch(e) {
            // don't care
        }
    }

    static import(str: string, options = {}): ImportResult|undefined {
        const opt = {
            assumeGCAbove: 300,
            ...options
        }

        const toCoord = (str: string) => {
            // {sign}HHHMM.SSS (no padding zeros it seems, e.g. can be "-12345.678" or "0")
            const tmpf = Number.parseFloat(str) / 100;
            return Math.trunc(tmpf) + ((tmpf % 1) * 100 / 60);
        }

        try {
            const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature> = [];

            const doc = RTEConverter.check(str);
            if (!doc) return;
            const waypoints = doc.getElementsByTagName('waypoint');
            let prevCoords: [number, number]|null = null;

            const pilotPoints: VoyageRoutePointFeature[] = [];
            let lineType: Element|null = null;
            Array.from(waypoints).forEach(waypoint => {
                const coords: [number, number] = [
                    toCoord(waypoint.querySelector('longitude')!.textContent!),
                    toCoord(waypoint.querySelector('latitude')!.textContent!)
                ];

                const name = waypoint.querySelector('details');
                const minSpeed = waypoint.querySelector('MinSpeed');
                const maxSpeed = waypoint.querySelector('MaxSpeed');
                const isPilot = waypoint.querySelector('isPilot');

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties: {
                        type: 'Hard',
                        name: name ? name.textContent! : '',
                        speed_minimum: minSpeed ? Number.parseFloat(minSpeed.textContent!)*KtsToMps : undefined,
                        speed_maximum: maxSpeed ? Number.parseFloat(maxSpeed.textContent!)*KtsToMps : undefined,
                        pilotPoint: isPilot && isPilot.textContent === 'true' ? true : undefined
                    }
                };

                if (wPoint.properties.pilotPoint) {
                    pilotPoints.push(wPoint);
                }

                if (prevCoords) {
                    const dist = haversineDist(prevCoords, coords) / NMToMeters; // Accept minimal error on dist, assuming GC for RL

                    let isGC = true;
                    if (lineType) {
                        isGC = lineType.textContent === 'GC';
                    } else if (dist < opt.assumeGCAbove) {
                        isGC = false;
                    }

                    route_geometry.push({
                            type: 'Feature',
                            geometry: {
                                coordinates: [prevCoords, coords],
                                type: 'LineString'
                            },
                            properties: {
                                leg_type: isGC ? 'GreatCircle' : 'Rhumbline'
                            }
                    });
                }

                route_geometry.push(wPoint);

                // The RTE route has GC/RL marked on the source wp
                lineType = waypoint.querySelector('type');

                prevCoords = coords;
            });

            const extras = RTEConverter._extractExtras(doc);

            // Add pilot point info
            if (pilotPoints.length === 1) {
                // Assume arrival pilot point
                extras.arrivalPilotPosition = pilotPoints[0].geometry.coordinates;
            } else if (pilotPoints.length >= 2) {
                extras.departurePilotPosition = pilotPoints[0].geometry.coordinates;
                extras.arrivalPilotPosition = pilotPoints[pilotPoints.length-1].geometry.coordinates;
            }

            return {
                route: {route_geometry,
                    passagePlan: {
                        description: "",
                        reminders: extras.passagePlan.reminders,
                        notEcdisCompliant: false,
                        encCellsUsed: extras.passagePlan.encCellsUsed,
                        booksAndTables: extras.passagePlan.booksAndTables,
                        chartsAndLightsNotes: extras.passagePlan.chartsAndLightsNotes,
                        sailingDirections: "",
                        listOfRadioSignals: "",
                        listOfLights: "",
                        gpsEcdisRadarMap: "",
                        paperCharts: "",
                        pubCorrectedToWeek: "", 
                        tideTables: "",
                        publicationsNotes: "", 
                        mastersOrders: "",
                        safetyMessages: {
                            navareaRegions: "",
                            navtexStations: ""
                        },
                        weatherInfo: {
                            navareaRegions: "",
                            navtexStations: "",
                            info: ""
                        },
                        equipmentStatus: {
                            operationalCondition: "",
                            failures: ""
                        },
                        weatherRouting: {
                            required: false,            
                            acknowledged: false,     
                            pilotPointAcknowledged: false 
                        }
                    } },
                extras
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static _extractExtras(doc: Document): { [index:string]: any } {
        // reminders
        let reminders, booksAndTables, chartsAndLightsNotes, encCellsUsed;
        let el = doc.querySelector('reminders');
        if (el) {
            reminders = Array.from(el.querySelectorAll('Description')).map(d => d.textContent).join('\n');
        }

        el = doc.querySelector('AdmiraltyListOfLights');
        if (el) chartsAndLightsNotes = el.textContent;

        el = doc.querySelector('PilotBooks');
        if (el) booksAndTables = el.textContent;

        el = doc.querySelector('ChartsUsed');
        if (el) encCellsUsed = el.textContent;

        el = doc.querySelector('OverviewCharts');
        if (el) encCellsUsed = encCellsUsed ? `${encCellsUsed}, ${el.textContent}` : el.textContent;

        return {
            passagePlan: {
                reminders,
                chartsAndLightsNotes,
                booksAndTables,
                encCellsUsed
            }
        }
    }

    static export(plan: VoyagePlan) {
        console.warn('RTEConverter.export missing!');
    }
}